import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/common/Button';
import { PanelWaiting } from '@/components/hub/PanelNotice';
import { useStalled } from '@/hooks/useStalled';
import { guastoTavoliMieiOf, type TavoloMio } from '@/lib/burraco/tavoliMiei';
import { useGetMyBurracoTablesQuery } from '@/services/limuloApi';

/**
 * «I MIEI TAVOLI / RIPRENDI» della Hub del Burraco (PR1 dell'ingresso; condizioni Z8-Z10 di tma-orcb): i tavoli ancora vivi di chi guarda, dal BFF di Limulo
 * (`burraco.table.mine.list`, `lib/burraco/tavoliMiei.ts`). «Riprendi» porta alla rotta che esiste GIA', `/burraco/tavolo/:id`, col `tableId` della risposta:
 * il tavolo chiede un ticket NUOVO come sempre (`useTavolo`). Nessuna rotta nuova, nessuna scrittura.
 *
 * I CINQUE esiti, ognuno con la sua frase (un elenco senza righe deve dire QUALE dei casi e):
 *  · in lettura — lo spinner, e dopo `useStalled` la riga «ci mette piu del previsto» (una transizione, non un verdetto);
 *  · `non-disponibile` — SOLO `424 E_QLASH_CAPABILITY_DISABLED`: lo dice, senza «Riprova» (riprovare non serve);
 *  · `rete` / `errore` — «non riesco a leggere i tuoi tavoli» con «Riprova». Il testo del server non si mostra mai;
 *  · una risposta che non ha la forma attesa — e un `errore`, mai «nessun tavolo» (`burracoTavoliMieiOf` rende `ok: false`);
 *  · elenco vuoto vero — «Non hai tavoli aperti.».
 * ⛔ Il vuoto si afferma solo davanti a un array vuoto vero; non c'e un errore travestito da vuoto.
 */
export function TavoliMiei() {
  const { t } = useTranslation('burraco');
  const navigate = useNavigate();
  // Si rilegge a ogni apertura della Hub (un tavolo vive poco: un elenco di prima porterebbe a un tavolo che non c'e piu).
  const { data, error, isFetching, refetch } = useGetMyBurracoTablesQuery(undefined, { refetchOnMountOrArgChange: true });
  const lenta = useStalled(data === undefined && error === undefined);

  let corpo;
  if (error !== undefined && data === undefined) {
    corpo = <Avviso guasto={guastoTavoliMieiOf(error)} ripetendo={isFetching} onRiprova={() => void refetch()} />;
  } else if (data === undefined) {
    corpo = <PanelWaiting slow={lenta} className="h-auto py-4" />;
  } else if (!data.ok) {
    corpo = <Avviso guasto="errore" ripetendo={isFetching} onRiprova={() => void refetch()} />;
  } else if (data.tavoli.length === 0) {
    corpo = (
      <p data-tavoli-vuoto className="rounded-xl bg-bg-secondary px-4 py-3 text-sm text-fg-muted">
        {t('hub.tavoli.vuoto')}
      </p>
    );
  } else {
    corpo = (
      <ul className="flex flex-col gap-2">
        {data.tavoli.map((tavolo) => (
          <RigaTavolo key={tavolo.tableId} tavolo={tavolo} onRiprendi={() => navigate(`/burraco/tavolo/${tavolo.tableId}`)} />
        ))}
      </ul>
    );
  }

  return (
    <section data-testid="burraco-tavoli-miei" aria-labelledby="burraco-tavoli-titolo" className="flex shrink-0 flex-col gap-2">
      <h2 id="burraco-tavoli-titolo" className="text-xs font-bold uppercase tracking-wide text-fg-muted">
        {t('hub.tavoli.titolo')}
      </h2>
      {corpo}
    </section>
  );
}

function RigaTavolo({ tavolo, onRiprendi }: { tavolo: TavoloMio; onRiprendi: () => void }) {
  const { t } = useTranslation('burraco');
  const formato = tavolo.posti === 4 ? t('stage.formato.2v2') : tavolo.posti === 2 ? t('stage.formato.1v1') : null;
  const stato = tavolo.attivo ? t('hub.tavoli.inCorso') : t('hub.tavoli.inAttesa');
  return (
    <li data-tavolo={tavolo.tableId} className="card-edge flex items-center gap-3 rounded-xl bg-bg-secondary p-3">
      {/* Il testo VA A CAPO (a 360 px accanto al bottone una riga sola si taglierebbe): `min-w-0` + `break-words`, mai `truncate`. */}
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="break-words text-sm font-semibold leading-5 text-fg">{t('stage.nomeTavoloVero')}</span>
        <span className="break-words text-xs leading-4 text-fg-muted">{formato === null ? stato : `${formato} · ${stato}`}</span>
      </span>
      {/* Taglia `md` (44 px di altezza DISEGNATA, non solo l'area di tocco estesa): la misura del banco la legge dal rettangolo. */}
      <Button variant="primary" className="shrink-0" onClick={onRiprendi}>
        {t('hub.tavoli.riprendi')}
      </Button>
    </li>
  );
}

function Avviso({ guasto, ripetendo, onRiprova }: { guasto: 'non-disponibile' | 'rete' | 'errore'; ripetendo: boolean; onRiprova: () => void }) {
  const { t } = useTranslation('burraco');
  return (
    <div role="status" data-tavoli-guasto={guasto} className="flex flex-col items-start gap-2 rounded-xl bg-bg-secondary px-4 py-3">
      <p className="text-sm text-fg-muted">{t(`hub.tavoli.guasto.${guasto}`)}</p>
      {guasto !== 'non-disponibile' && (
        <Button variant="secondary" disabled={ripetendo} onClick={onRiprova}>
          {t('hub.tavoli.riprova')}
        </Button>
      )}
    </div>
  );
}
